import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import api from '#adapter/api/main';
import { rtkApi as rtkApiStatWords } from '#adapter/api/campaigns/v5/stat-words';
import { rtkApi as rtkApiWords } from '#adapter/api/campaigns/v5/words';
import apiCampaigns from '#adapter/api/campaigns';

import { IWord, TTabType, TWordsTypes, TDuplicatesType } from '#component/keywords/types';
import {
    mapIWordToFull,
    mapIWordToSelected,
    getUpdatedKeywords,
    mapIResponseToIState,
    mapWordsToIState,
    mapExcludedAndPlusToIState,
    getSortWords
} from '#component/keywords/services';

import { ETabType } from '../const';

interface IState {
    words: TWordsTypes & { fixed: boolean },
    duplicatesType?: TDuplicatesType,
    duplicateWords?: Array<string>,
    isOpenRemoveDuplicatesModal: boolean,
    textRemoveDuplicatesModal?: string
}

const initialState: IState = {
    words: {
        excluded: [],
        keywords: [],
        phrase  : [],
        pluse   : [],
        strong  : [],
        fixed   : false
    },
    duplicatesType             : undefined,
    duplicateWords             : undefined,
    isOpenRemoveDuplicatesModal: false,
    textRemoveDuplicatesModal  : undefined
};

export const wordTabs = createSlice({
    initialState,
    name    : 'keywords',
    reducers: {
        reset      : () => initialState,
        updateFixed: (state, action: PayloadAction<boolean>) => {
            // eslint-disable-next-line no-param-reassign
            state.words.fixed = action.payload;
        },
        changeSelection: (state, {
            payload: {
                word,
                activeTabKey
            }
        }: PayloadAction<{ word: IWord, activeTabKey: TTabType }>) => {
            // eslint-disable-next-line no-param-reassign
            state.words[activeTabKey] = state.words[activeTabKey]
                .map((el) => (el.keyword === word.keyword ? { ...el, selected: !el.selected } : el));
        },
        selectAll: (state, {
            payload: {
                activeTabKey
            }
        }: PayloadAction<{ activeTabKey: TTabType }>) => ({
            ...state,
            words: {
                ...state.words,
                [activeTabKey]: state.words[activeTabKey].map(mapIWordToSelected)
            }
        }),
        deselectAll: (state, { payload: { activeTabKey } }: PayloadAction<{ activeTabKey: TTabType }>) => (
            {
                ...state,
                words: {
                    ...state.words,
                    [activeTabKey]: state.words[activeTabKey].map(mapIWordToFull)
                }
            }
        ),
        // actions для изменения фраз на странице редактирования
        excludedEditWords: (state, { payload: { excluded } }: PayloadAction<{ excluded: Array<string> }>) => {
            const excludedSet = new Set(excluded);
            const newKeywords = [...state.words.keywords.map((word) => {
                if(excludedSet.has(word.keyword)) {
                    return { ...word, position: ETabType.EXCLUDED, selected: false };
                }

                return word;
            })];
            const newExcluded = excluded.map((word) => ({ keyword: word, active: false, selected: false }));
            const sortExcluded = getSortWords(newExcluded);

            return {
                ...state,
                words: {
                    ...state.words,
                    excluded: sortExcluded,
                    keywords: newKeywords
                }
            };
        },
        fixedEditWords: (state, { payload: { plusWords } }: PayloadAction<{ plusWords: Array<string> }>) => {
            const pluseSet = new Set(plusWords);
            const newKeywords = [...state.words.keywords.map((word) => {
                if(pluseSet.has(word.keyword) && !!state.words.fixed) {
                    return { ...word, position: ETabType.PLUSE, selected: false };
                }

                return word.selected ? { ...word, selected: false } : word;
            })];
            const newPlusWords = plusWords.map((word) => ({ keyword: word, active: false, selected: false }));
            const sortPlusWords = getSortWords(newPlusWords);

            return {
                ...state,
                words: {
                    ...state.words,
                    pluse   : sortPlusWords,
                    keywords: newKeywords,
                    fixed   : true
                }
            };
        },
        returnInEditKeywordsFromFixed: (state, { payload: { plusWords } }: PayloadAction<{ plusWords: Array<string> }>) => {
            const newPlusWords = plusWords.map((word) => ({ keyword: word, active: false, selected: false }));
            const sortPlusWords = getSortWords(newPlusWords);

            return {
                ...state,
                words: {
                    ...state.words,
                    pluse: sortPlusWords
                }
            };
        },
        // actions для изменения фраз на странице создания
        excludedWords: (state, { payload: { excluded } }: PayloadAction<{ excluded: Array<IWord> }>) => {
            const newExcluded = [...excluded.map((word) => ({ ...word, selected: false })), ...state.words.excluded];
            const sortExcluded = getSortWords(newExcluded);
            const newKeywords = state.words.keywords.filter((word) => !word.selected);

            return {
                ...state,
                words: {
                    ...state.words,
                    excluded: sortExcluded,
                    keywords: newKeywords
                }
            };
        },
        returnInKeywordsFromExcluded: (state, { payload: { keywords } }: PayloadAction<{ keywords: Array<IWord> }>) => {
            const newKeywords = [...keywords.map((word) => ({ ...word, selected: false })), ...state.words.keywords];
            const newExcluded = state.words.excluded.filter((word) => !word.selected);

            return {
                ...state,
                words: {
                    ...state.words,
                    keywords: newKeywords,
                    excluded: newExcluded
                }
            };
        },
        returnInKeywordsFromFixed: (state, { payload: { keywords } }: PayloadAction<{ keywords: Array<IWord> }>) => {
            const newKeywords = [...keywords.map((word) => ({ ...word, selected: false })), ...state.words.keywords];
            const newFixed = state.words.pluse.filter((word) => !word.selected);

            return {
                ...state,
                words: {
                    ...state.words,
                    keywords: newKeywords,
                    pluse   : newFixed
                }
            };
        },
        fixedWords: (state, { payload: { pluse } }: PayloadAction<{ pluse: Array<IWord> }>) => {
            const newFixed = [...pluse.map((word) => ({ ...word, selected: false })), ...state.words.pluse];
            const sortNewFixed = getSortWords(newFixed);
            const newKeywords = state.words.keywords.filter((word) => !word.selected);

            return {
                ...state,
                words: {
                    ...state.words,
                    keywords: newKeywords,
                    pluse   : sortNewFixed
                }
            };
        },
        fixedQuery: (state, { payload: { fixed } }: PayloadAction<{ fixed: boolean }>) => ({
            ...state,
            words: { ...state.words, fixed }
        }),
        removeStrongWords: (state, { payload: { strong } }: PayloadAction<{ strong: Array<IWord> }>) => ({
            ...state,
            words: { ...state.words, strong }
        }),
        removePhraseWords: (state, { payload: { phrase } }: PayloadAction<{ phrase: Array<IWord> }>) => ({
            ...state,
            words: { ...state.words, phrase }
        }),
        addCustomKeywords: (state, { payload: { keywords } }: PayloadAction<{ keywords: Array<IWord> }>) => ({
            ...state,
            words: { ...state.words, keywords: getUpdatedKeywords(keywords, state.words.keywords) }
        }),
        addCustomExcluded: (state, { payload: { excluded } }: PayloadAction<{ excluded: Array<IWord> }>) => ({
            ...state,
            words: { ...state.words, excluded: [...excluded, ...state.words.excluded] }
        }),
        addDuplicateWords: (state, { payload: {
            isOpenRemoveDuplicatesModal,
            textRemoveDuplicatesModal,
            duplicatesType,
            duplicateWords
        } }: PayloadAction<{
            isOpenRemoveDuplicatesModal: boolean,
            textRemoveDuplicatesModal?: string,
            duplicatesType?: TDuplicatesType,
            duplicateWords: Array<string>
        }>) => ({
            ...state,
            duplicatesType,
            duplicateWords,
            isOpenRemoveDuplicatesModal,
            textRemoveDuplicatesModal
        }),
        removeDuplicateWords: (state) => ({
            ...state,
            duplicatesType             : undefined,
            duplicateWords             : undefined,
            isOpenRemoveDuplicatesModal: false,
            textRemoveDuplicatesModal  : undefined
        })
    },
    extraReducers: (builder) => {
        builder.addMatcher(api.endpoints.searchCatalogStatWordsGet.matchFulfilled, (state, { payload }) => ({
            ...state,
            words: {
                ...mapIResponseToIState(payload, true)
            }
        }));
        builder.addMatcher(rtkApiStatWords.endpoints.getApiV5StatWords.matchFulfilled, (state, { payload }) => ({
            ...state,
            words: {
                ...state.words,
                keywords: mapWordsToIState(payload)
            }
        }));
        builder.addMatcher(rtkApiWords.endpoints.getApiV5Words.matchFulfilled, (state, { payload }) => ({
            ...state,
            words: {
                ...state.words,
                ...mapExcludedAndPlusToIState(payload)
            }
        }));
        builder.addMatcher(apiCampaigns.endpoints.v2AutoAdvertIdStatWordsGet.matchFulfilled, (state, { payload }) => ({
            ...state,
            words: {
                ...mapIResponseToIState(payload)
            }
        }));
    }
});

export default wordTabs.reducer;
